import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  GetDashboardDepartments,
  GetDashboardSummary,
  UploadDashboard,
} from "../../api/dashboard";
import { GetAcativeRestaurants } from "../../api/restaurant";
import { Me } from "../../api/users";
import { PostGet } from "../../api/post";
import { KADRLevel } from "../../constants";
import ColHrDashboard from "./ColHrDashboard";
import FinanceDashboard from "./FinanceDashboard";
import HrDashboard from "./HrDashboard";
import LegalDashboard from "./LegalDashboard";
import OpsDashboard from "./OpsDashboard";

const UPLOADABLE_CODES = new Set(["finance", "legal", "kadr", "hr", "ops"]);
const RESTAURANT_FILTER_CODES = new Set(["finance", "hr", "ops"]);
const DEPARTMENT_HINTS = {
  finance: "Сводка по загрузкам финансового отдела",
  legal: "Сводка по загрузкам юридического отдела",
  kadr: "Сводка по загрузкам отдела кадров",
  hr: "Сводка по загрузкам HR из COL",
  ops: "Сводка OPS из ночного парсера",
};

function monthStartIso() {
  const date = new Date();
  date.setDate(1);
  return toIso(date);
}

function todayIso() {
  return toIso(new Date());
}

function toIso(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function errorText(payload, fallback) {
  if (!payload) return fallback;
  if (typeof payload === "string") return payload;
  if (typeof payload.detail === "string") return payload.detail;
  return fallback;
}

const ReportDashboard = ({ token }) => {
  const [accessLevel, setAccessLevel] = useState(0);
  const [departments, setDepartments] = useState([]);
  const [departmentId, setDepartmentId] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [restaurantId, setRestaurantId] = useState(0);
  const [dateFrom, setDateFrom] = useState(monthStartIso);
  const [dateTo, setDateTo] = useState(todayIso);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [date, setDate] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const fileInputRef = useRef(null);

  const isKadr = accessLevel === KADRLevel;
  const selectedDepartment = departments.find(
    (item) => item.id === departmentId,
  );
  const canUpload = isKadr && UPLOADABLE_CODES.has(selectedDepartment?.code);
  const canSubmitUpload = canUpload && Boolean(date && file) && !sending;

  useEffect(() => {
    async function init() {
      try {
        const user = await Me(token);
        const post = await PostGet(token, user.post_id);
        setAccessLevel(post.access_level);
        const [deptRows, restaurantRows] = await Promise.all([
          GetDashboardDepartments(token),
          GetAcativeRestaurants(token),
        ]);
        const list = Array.isArray(deptRows) ? deptRows : [];
        setDepartments(list);
        const preferred =
          list.find((item) => item.code === "finance") ||
          list.find((item) => item.has_data) ||
          list[0];
        if (preferred) setDepartmentId(preferred.id);
        setRestaurants(
          (Array.isArray(restaurantRows) ? restaurantRows : []).filter(
            (item) => item?.is_active !== false && item?.name !== "Офис",
          ),
        );
      } catch (err) {
        setError(
          errorText(err?.response?.data, "Не удалось открыть дашборд"),
        );
      } finally {
        setLoading(false);
      }
    }
    init();
  }, [token]);

  useEffect(() => {
    if (!departmentId || !dateFrom || !dateTo) return undefined;
    let cancelled = false;

    async function loadSummary() {
      if (dateFrom > dateTo) {
        setError("Начало периода не может быть позже конца");
        setSummary(null);
        return;
      }
      setLoadingSummary(true);
      setError("");
      const restaurantFilter =
        RESTAURANT_FILTER_CODES.has(
          departments.find((item) => item.id === departmentId)?.code,
        )
          ? restaurantId || undefined
          : undefined;
      try {
        const data = await GetDashboardSummary(
          token,
          departmentId,
          dateFrom,
          dateTo,
          restaurantFilter,
        );
        if (!cancelled) setSummary(data);
      } catch (err) {
        if (!cancelled) {
          setSummary(null);
          setError(
            errorText(err?.response?.data, "Не удалось загрузить показатели"),
          );
        }
      } finally {
        if (!cancelled) setLoadingSummary(false);
      }
    }

    loadSummary();
    return () => {
      cancelled = true;
    };
  }, [token, departmentId, dateFrom, dateTo, restaurantId, departments]);

  const restaurantOptions = useMemo(
    () =>
      [...restaurants].sort((a, b) =>
        String(a.name).localeCompare(String(b.name), "ru"),
      ),
    [restaurants],
  );

  function onFileChange(event) {
    setFile(event.target.files?.[0] || null);
    setSuccess("");
  }

  async function onSubmit(event) {
    event.preventDefault();
    setError("");
    setSuccess("");
    if (!date || !file) {
      setError(
        selectedDepartment?.code === "ops"
          ? "Укажите дату и файл .txt"
          : "Укажите дату отчёта и файл .xlsx",
      );
      return;
    }
    setSending(true);
    try {
      const data = await UploadDashboard(token, date, file);
      const skipped = data?.skipped_restaurants || [];
      setSuccess(
        skipped.length
          ? `Файл загружен. Не найдены рестораны: ${skipped.join(", ")}`
          : "Файл загружен",
      );
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      const restaurantFilter = RESTAURANT_FILTER_CODES.has(
        selectedDepartment?.code,
      )
        ? restaurantId || undefined
        : undefined;
      const next = await GetDashboardSummary(
        token,
        departmentId,
        dateFrom,
        dateTo,
        restaurantFilter,
      );
      setSummary(next);
    } catch (err) {
      setError(
        errorText(err?.response?.data, "Не удалось загрузить файл"),
      );
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="dashboard-page">
      <aside className="dashboard-sidebar">
        <Link to="/reports" className="dashboard-back">
          ← К отчётам
        </Link>
        <p className="dashboard-sidebar-title">Отделы</p>
        <nav className="dashboard-nav" aria-label="Отделы">
          {departments.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`dashboard-nav-item${
                item.id === departmentId ? " is-active" : ""
              }`}
              onClick={() => setDepartmentId(item.id)}
            >
              <span>{item.name}</span>
              {item.has_data ? <i className="dashboard-nav-dot" /> : null}
            </button>
          ))}
        </nav>
      </aside>

      <div className="dashboard-main">
        <header className="dashboard-header">
          <div>
            <h1>{selectedDepartment?.name || "Дашборд"}</h1>
            <p className="reports-hint">
              {DEPARTMENT_HINTS[selectedDepartment?.code] || "Показатели отдела"}
            </p>
          </div>
          <div className="dashboard-filters">
            <label>
              С
              <input
                type="date"
                value={dateFrom}
                onChange={(event) => setDateFrom(event.target.value)}
              />
            </label>
            <label>
              По
              <input
                type="date"
                value={dateTo}
                onChange={(event) => setDateTo(event.target.value)}
              />
            </label>
            {RESTAURANT_FILTER_CODES.has(selectedDepartment?.code) ? (
              <label>
                Ресторан
                <select
                  value={restaurantId}
                  onChange={(event) =>
                    setRestaurantId(Number(event.target.value) || 0)
                  }
                >
                  <option value={0}>Все рестораны</option>
                  {restaurantOptions.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>
        </header>

        {canUpload ? (
          <section className="dashboard-upload-card">
            <button
              type="button"
              className="dashboard-upload-toggle"
              onClick={() => setUploadOpen((open) => !open)}
            >
              {uploadOpen
                ? "Скрыть загрузку файла"
                : selectedDepartment?.code === "ops"
                  ? "Загрузить .txt"
                  : "Загрузить .xlsx"}
            </button>
            {uploadOpen ? (
              <form className="dashboard-upload-form" onSubmit={onSubmit}>
                {selectedDepartment?.code === "ops" ? (
                  <p className="dashboard-upload-hint">
                    Даты берутся из текста отчёта. Поле ниже нужно только чтобы
                    отправить форму.
                  </p>
                ) : null}
                <input
                  className="form-control"
                  type="date"
                  value={date}
                  onChange={(event) => setDate(event.target.value)}
                />
                <input
                  ref={fileInputRef}
                  className="form-control"
                  type="file"
                  accept={
                    selectedDepartment?.code === "ops"
                      ? ".txt,text/plain"
                      : ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  }
                  onChange={onFileChange}
                />
                <button
                  className="btn dashboard-upload-submit"
                  type="submit"
                  disabled={!canSubmitUpload}
                >
                  {sending ? "Отправка..." : "Отправить"}
                </button>
              </form>
            ) : null}
          </section>
        ) : null}

        {error ? <div className="alert alert-danger">{error}</div> : null}
        {success ? <div className="alert alert-success">{success}</div> : null}

        {loading ? (
          <div className="reports-loading">
            <div className="spinner-border profile-spinner" role="status">
              <span className="visually-hidden">Загрузка...</span>
            </div>
            <p>Загрузка...</p>
          </div>
        ) : loadingSummary ? (
          <div className="reports-loading">
            <div className="spinner-border profile-spinner" role="status">
              <span className="visually-hidden">Загрузка...</span>
            </div>
            <p>Считаем показатели...</p>
          </div>
        ) : summary?.ready && summary?.has_data ? (
          summary.department?.code === "legal" ? (
            <LegalDashboard summary={summary} />
          ) : summary.department?.code === "kadr" ? (
            <HrDashboard summary={summary} />
          ) : summary.department?.code === "hr" ? (
            <ColHrDashboard summary={summary} />
          ) : summary.department?.code === "ops" ? (
            <OpsDashboard summary={summary} />
          ) : (
            <FinanceDashboard summary={summary} />
          )
        ) : (
          <div className="reports-empty">
            {summary?.message || "Нет данных за выбранный период"}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportDashboard;
